// src/services/feedbackService.js

const API_BASE_URL = 'https://nlngraclub.com/nlnapi';

const getToken = () => localStorage.getItem('nlngra_token');
const getUser = () => {
    const userStr = localStorage.getItem('nlngra_user');
    if (userStr) {
        try {
            return JSON.parse(userStr);
        } catch (e) {
            return null;
        }
    }
    return null;
};

const fetchWithTimeout = (url, options, timeout = 30000) => {
    return Promise.race([
        fetch(url, options),
        new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Request timeout')), timeout)
        )
    ]);
};

const handleResponse = async (response, endpoint) => {
    const text = await response.text();

    // Clean response from PHP warnings
    let cleanResponse = text.replace(/<br\s*\/?>\s*<b>Warning<\/b>.*?(?=<br|<\\?|\{)/gis, '');
    cleanResponse = cleanResponse.replace(/<b>Warning<\/b>.*?<br\s*\/?>/gis, '');
    cleanResponse = cleanResponse.replace(/<b>Notice<\/b>.*?<br\s*\/?>/gis, '');
    cleanResponse = cleanResponse.replace(/<[^>]*>/g, '');

    const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
        try {
            return JSON.parse(jsonMatch[0]);
        } catch (e) {
            console.error(`JSON parse error for ${endpoint}:`, e);
            return { success: false, message: 'Invalid JSON response' };
        }
    }

    return { success: false, message: 'Invalid server response' };
};

// Get CSRF token
const getCSRFToken = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/feedback.php?action=csrf_token`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();
        if (data.success) {
            localStorage.setItem('csrf_token', data.csrf_token);
            return data.csrf_token;
        }
    } catch (error) {
        console.error('Error getting CSRF token:', error);
    }
    return null;
};

export const feedbackService = {
    // Submit feedback with CSRF protection
    submitFeedback: async (rating, comment, category = 'general') => {
        try {
            const user = getUser();
            const token = getToken();
            let csrfToken = localStorage.getItem('csrf_token');

            if (!csrfToken) {
                csrfToken = await getCSRFToken();
            }

            if (!user) {
                return { success: false, message: 'Please log in to submit feedback' };
            }

            // Validate inputs on client side
            if (rating < 1 || rating > 5) {
                return { success: false, message: 'Invalid rating' };
            }

            if (!comment || comment.trim().length < 10) {
                return { success: false, message: 'Please provide more detailed feedback (minimum 10 characters)' };
            }

            if (comment.length > 5000) {
                return { success: false, message: 'Comment is too long. Maximum 5000 characters.' };
            }

            const feedbackData = {
                member_id: user.id,
                member_name: user.full_name || `${user.first_name} ${user.last_name}`,
                member_email: user.email,
                rating: parseInt(rating),
                comment: comment.trim(),
                category: category,
                submitted_at: new Date().toISOString()
            };

            const response = await fetchWithTimeout(`${API_BASE_URL}/feedback.php?action=submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : '',
                    'X-CSRF-Token': csrfToken || ''
                },
                body: JSON.stringify(feedbackData)
            }, 15000);

            return await handleResponse(response, 'Submit Feedback');
        } catch (error) {
            console.error('Submit feedback error:', error);
            return { success: false, message: error.message || 'Failed to submit feedback' };
        }
    },

    // Get my feedbacks (for regular users)
    getMyFeedbacks: async () => {
        console.log('📝 [getMyFeedbacks] Called');
        try {
            const user = getUser();
            const token = getToken();
            
            console.log('📝 [getMyFeedbacks] User:', user?.id, 'Token exists:', !!token);

            if (!user) {
                console.log('📝 [getMyFeedbacks] No user found');
                return { success: false, feedbacks: [] };
            }

            const url = `${API_BASE_URL}/feedback.php?action=my_feedbacks&member_id=${user.id}&_t=${Date.now()}`;
            console.log('📝 [getMyFeedbacks] Fetching URL:', url);
            
            const response = await fetchWithTimeout(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                }
            }, 10000);
            
            console.log('📝 [getMyFeedbacks] Response status:', response.status);
            const result = await handleResponse(response, 'Get My Feedbacks');
            console.log('📝 [getMyFeedbacks] Result:', result);
            
            return result;
        } catch (error) {
            console.error('Get my feedbacks error:', error);
            return { success: false, feedbacks: [] };
        }
    },

    // Get all feedbacks (admin only)
    getAllFeedbacks: async (filters = {}) => {
        console.log('📝 [Admin] getAllFeedbacks called with filters:', filters);
        
        try {
            const token = getToken();
            console.log('📝 [Admin] Token exists:', !!token);
            
            const params = new URLSearchParams({
                action: 'all_feedbacks',
                _t: Date.now(),
                ...filters
            }).toString();
            
            const url = `${API_BASE_URL}/feedback.php?${params}`;
            console.log('📝 [Admin] Fetching URL:', url);
            
            const response = await fetchWithTimeout(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                }
            }, 15000);
            
            console.log('📝 [Admin] Response status:', response.status);
            console.log('📝 [Admin] Response OK?', response.ok);
            
            if (!response.ok) {
                console.error('📝 [Admin] HTTP Error:', response.status, response.statusText);
                return { success: false, feedbacks: [], stats: null, error: `HTTP ${response.status}: ${response.statusText}` };
            }
            
            const result = await handleResponse(response, 'Get All Feedbacks');
            console.log('📝 [Admin] Result success:', result.success);
            console.log('📝 [Admin] Feedbacks count:', result.feedbacks?.length || 0);
            
            if (!result.success) {
                console.error('📝 [Admin] API Error:', result.message);
            }
            
            return result;
        } catch (error) {
            console.error('📝 [Admin] Get all feedbacks error:', error);
            return { success: false, feedbacks: [], stats: null, error: error.message };
        }
    },

    // Get feedback stats (admin only)
    getFeedbackStats: async () => {
        console.log('📝 [Admin] getFeedbackStats called');
        
        try {
            const token = getToken();
            const url = `${API_BASE_URL}/feedback.php?action=stats&_t=${Date.now()}`;
            console.log('📝 [Admin] Stats URL:', url);
            
            const response = await fetchWithTimeout(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                }
            }, 10000);
            
            console.log('📝 [Admin] Stats response status:', response.status);
            const result = await handleResponse(response, 'Get Feedback Stats');
            console.log('📝 [Admin] Stats result:', result);
            
            return result;
        } catch (error) {
            console.error('Get feedback stats error:', error);
            return { success: false, stats: null };
        }
    },

    // Update feedback status (admin only)
    updateFeedbackStatus: async (feedbackId, status, adminResponse = '') => {
        try {
            const token = getToken();
            const response = await fetchWithTimeout(`${API_BASE_URL}/feedback.php?action=update_status`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                },
                body: JSON.stringify({
                    feedback_id: feedbackId,
                    status: status,
                    admin_response: adminResponse
                })
            }, 10000);

            return await handleResponse(response, 'Update Feedback Status');
        } catch (error) {
            console.error('Update feedback status error:', error);
            return { success: false, message: error.message };
        }
    },

    // Delete feedback (admin only)
    deleteFeedback: async (feedbackId) => {
        try {
            const token = getToken();
            const response = await fetchWithTimeout(`${API_BASE_URL}/feedback.php?action=delete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                },
                body: JSON.stringify({ feedback_id: feedbackId })
            }, 10000);

            return await handleResponse(response, 'Delete Feedback');
        } catch (error) {
            console.error('Delete feedback error:', error);
            return { success: false, message: error.message };
        }
    },

    // Bulk delete feedbacks (admin only)
    bulkDeleteFeedbacks: async (feedbackIds) => {
        try {
            const token = getToken();

            if (!feedbackIds || !Array.isArray(feedbackIds) || feedbackIds.length === 0) {
                return { success: false, message: 'No feedback IDs provided' };
            }

            if (feedbackIds.length > 50) {
                return { success: false, message: 'Maximum 50 feedbacks can be deleted at once' };
            }

            const response = await fetchWithTimeout(`${API_BASE_URL}/feedback.php?action=bulk_delete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                },
                body: JSON.stringify({ feedback_ids: feedbackIds })
            }, 15000);

            return await handleResponse(response, 'Bulk Delete Feedbacks');
        } catch (error) {
            console.error('Bulk delete feedbacks error:', error);
            return { success: false, message: error.message };
        }
    },

    // Get feedback by ID (admin only)
    getFeedbackById: async (feedbackId) => {
        try {
            const token = getToken();
            const response = await fetchWithTimeout(`${API_BASE_URL}/feedback.php?action=get_feedback&id=${feedbackId}&_t=${Date.now()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                }
            }, 10000);

            return await handleResponse(response, 'Get Feedback By ID');
        } catch (error) {
            console.error('Get feedback by ID error:', error);
            return { success: false, feedback: null };
        }
    },

    // Export feedbacks to CSV (admin only)
    exportFeedbacksToCSV: async (filters = {}) => {
        try {
            const token = getToken();
            const params = new URLSearchParams({
                action: 'export_csv',
                _t: Date.now(),
                ...filters
            }).toString();

            const response = await fetchWithTimeout(`${API_BASE_URL}/feedback.php?${params}`, {
                method: 'GET',
                headers: {
                    'Authorization': token ? `Bearer ${token}` : ''
                }
            }, 20000);

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `feedbacks_export_${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                return { success: true, message: 'Export started successfully' };
            } else {
                return { success: false, message: 'Failed to export feedbacks' };
            }
        } catch (error) {
            console.error('Export feedbacks error:', error);
            return { success: false, message: error.message };
        }
    },

    // Get feedback categories with counts (admin only)
    getFeedbackCategories: async () => {
        try {
            const token = getToken();
            const response = await fetchWithTimeout(`${API_BASE_URL}/feedback.php?action=category_stats&_t=${Date.now()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                }
            }, 10000);

            return await handleResponse(response, 'Get Feedback Categories');
        } catch (error) {
            console.error('Get feedback categories error:', error);
            return { success: false, categories: [] };
        }
    },

    // Get my feedback stats (for regular users)
    getMyFeedbackStats: async () => {
        try {
            const user = getUser();
            const token = getToken();

            if (!user) {
                return { success: false, stats: null };
            }

            const response = await fetchWithTimeout(`${API_BASE_URL}/feedback.php?action=my_stats&member_id=${user.id}&_t=${Date.now()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                }
            }, 10000);

            return await handleResponse(response, 'Get My Feedback Stats');
        } catch (error) {
            console.error('Get my feedback stats error:', error);
            return { success: false, stats: null };
        }
    }
};

export default feedbackService;